import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Resource, UIComponent } from '../../types';

interface AudioBankComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

export const AudioBankComponent: React.FC<AudioBankComponentProps> = ({ resource, component, onAction }) => {
  const { t } = useTranslation();
  const [currentWord, setCurrentWord] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);

  const words = resource.words || [];

  // Check if speech synthesis is supported
  useEffect(() => {
    setSpeechSupported('speechSynthesis' in window);
  }, []);

  // Cleanup: stop any playing speech when component unmounts
  useEffect(() => {
    return () => {
      if (speechSupported) {
        window.speechSynthesis.cancel();
      }
    };
  }, [speechSupported]);

  const playWord = (word: string) => {
    if (!speechSupported) {
      console.warn('Speech synthesis not supported in this browser');
      return;
    }

    // Cancel any currently playing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(word);
    utterance.rate = 0.8; // Slightly slower for clarity
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => {
      setIsPlaying(true);
    };

    utterance.onend = () => {
      setIsPlaying(false);
    };

    utterance.onerror = () => {
      setIsPlaying(false);
      console.error('Speech synthesis error');
    };

    window.speechSynthesis.speak(utterance);
  };

  const handleWordClick = (word: string) => {
    setCurrentWord(word);
    playWord(word);
    onAction?.('play_audio', { word });
  };

  const handlePlayAll = () => {
    if (!speechSupported || words.length === 0) {
      return;
    }

    setIsPlaying(true);
    onAction?.('play_all_audio', { words });

    // Cancel any currently playing speech
    window.speechSynthesis.cancel();

    let currentIndex = 0;

    const playNextWord = () => {
      if (currentIndex >= words.length) {
        setIsPlaying(false);
        return;
      }

      const word = words[currentIndex];
      setCurrentWord(word);
      
      const utterance = new SpeechSynthesisUtterance(word);
      utterance.rate = 0.8;
      utterance.pitch = 1;
      utterance.volume = 1;

      utterance.onend = () => {
        currentIndex++;
        // Add a small pause between words
        setTimeout(() => {
          playNextWord();
        }, 500);
      };

      utterance.onerror = () => {
        setIsPlaying(false);
        console.error('Speech synthesis error');
      };

      window.speechSynthesis.speak(utterance);
    };

    playNextWord();
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="text-center mb-4">
        <h3 className="font-semibold text-gray-900 mb-2">{t('audioBank.audioBank')}</h3>
        <p className="text-sm text-gray-600">
          {words.length} {t('audioBank.wordsAvailable')}
        </p>
      </div>

      {currentWord && (
        <div className="bg-green-50 rounded-lg p-3 mb-4 border border-green-200">
          <div className="text-center">
            <div className="text-lg font-semibold text-green-800 mb-1">
              {currentWord}
            </div>
            <div className="text-sm text-green-600">
              {isPlaying ? '🔊 ' + t('audioBank.playing') : t('audioBank.clickToPlay')}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        {words.map((word: string, index: number) => (
          <button
            key={index}
            onClick={() => handleWordClick(word)}
            className="p-2 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors text-sm font-medium text-blue-800"
          >
            {word}
          </button>
        ))}
      </div>

      <div className="flex justify-center space-x-2">
        <button
          onClick={handlePlayAll}
          disabled={isPlaying || !speechSupported}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
        >
          {isPlaying ? t('audioBank.playing') : t('audioBank.playAll')}
        </button>
        {isPlaying && (
          <button
            onClick={() => {
              window.speechSynthesis.cancel();
              setIsPlaying(false);
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {t('audioBank.stop')}
          </button>
        )}
        <button
          onClick={() => {
            setCurrentWord('');
            window.speechSynthesis.cancel();
            setIsPlaying(false);
          }}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          {t('audioBank.clear')}
        </button>
      </div>

      {!speechSupported && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            ⚠️ {t('audioBank.speechSynthesisNotSupported')}
          </p>
        </div>
      )}
    </div>
  );
};
